import {
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  HomeIcon,
  UserGroupIcon,
  UsersIcon,
  XMarkIcon,
  MapIcon,
  MapPinIcon,
  ShieldCheckIcon,
  WrenchIcon,
  WrenchScrewdriverIcon,
  QrCodeIcon,
  ShoppingCartIcon,
  QueueListIcon,
  CogIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  ClipboardDocumentCheckIcon,
  DocumentChartBarIcon,
  CalculatorIcon,
  CircleStackIcon,
  TruckIcon,
  UserPlusIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import React, { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { BRAND_NAME, DEVELOPER_NAME } from "../config/Constants";
import { config } from "../config/environment";
import { RootState } from "../store";
import { setSidebarOpen, toggleSidebarCollapse } from "../store/slices/uiSlice";
import CompanyLogo from "./common/CompanyLogo";
import { usePermission, PermissionGuard } from "./common/PermissionGuard";
import Branches from "@/feature/branch/Branches";
import { useLocation } from "react-router-dom";
import { fetchBranchById, selectSelectedBranch } from "../feature/branch/branchSlice"


// const navigation = [
//   { name: "Dashboard", href: "/", icon: HomeIcon },
//   canRead && { name: "Branches", href: "/branches", icon: BuildingOfficeIcon },
//   { name: "Users", href: "/users", icon: UserGroupIcon },
//   { name: "Customers", href: "/customers", icon: UsersIcon },
//   { name: "Orders", href: "/orders", icon: ClipboardDocumentListIcon },
//   { name: "Sites", href: "/sites", icon: MapPinIcon },
//   // { name: "RBAC Management", href: "/rbac", icon: ShieldCheckIcon },
//   // { name: "Settings", href: "/settings", icon: Cog6ToothIcon },
// ].filter(Boolean);

const Sidebar: React.FC = memo(() => {
  const dispatch = useDispatch();
  const { sidebarCollapsed, sidebarOpen } = useSelector((state: RootState) => state.ui);
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const selectedBranch = useSelector(selectSelectedBranch);

  // Check orders permissions - menu shows if user has read, create, or update permission
  const canOrdersRead = usePermission('orders', 'read');
  const canOrdersCreate = usePermission('orders', 'create');
  const canOrdersUpdate = usePermission('orders', 'update');
  const canAccessOrders = canOrdersRead || canOrdersCreate || canOrdersUpdate;

  useEffect(() => {
    console.log("==>User:", user)
    console.log("==>selectedBranch:", selectedBranch)
    if (user?.branch_id) {
      dispatch(fetchBranchById(user.branch_id));
    }
  }, [user, dispatch])

  // Sidebar state is now stable during navigation

  const handleToggleCollapse = () => {
    dispatch(toggleSidebarCollapse());
  };

  const handleCloseSidebar = () => {
    dispatch(setSidebarOpen(false));
  };

  // Permission-based navigation items
  const navigationItems = [
    // { name: "Dashboard", href: "/", icon: HomeIcon, module: "dashboard", action: "read" },
    // { name: "Branches", href: "/branches", icon: BuildingOfficeIcon, module: "branches", action: "read" },
    { name: "Users", href: "/users", icon: UserGroupIcon, module: "users", action: "read" },
    { name: "Customers", href: "/customers", icon: UsersIcon, module: "customers", action: "read" },
    { name: "Contacts", href: "/contacts", icon: PhoneIcon, module: "contacts", action: "read" },
    { name: "Leads", href: "/leads", icon: UserPlusIcon, module: "leads", action: "read" },
    { name: "Daily Sales Dashboard", href: "/dashboard1", icon: ChartBarIcon, module: "dashboard", action: "read" },
    // { name: "Dashboard 2", href: "/dashboard2", icon: ChartBarIcon, module: "dashboard", action: "read" },
    { name: "Sites", href: "/sites", icon: MapPinIcon, module: "sites", action: "read" },
    { name: "Work Orders", href: "/orders", icon: ShoppingCartIcon, module: "orders", action: "read", requiresAnyPermission: true },
    // { name: "Dispatch", href: "/dispatch", icon: TruckIcon, module: "dispatch", action: "read" },
    // { name: "Templates", href: "/masters/workflow", icon: ClipboardDocumentListIcon, module: "workflows", action: "read" },
    // { name: "RBAC", href: "/rbac", icon: ShieldCheckIcon, module: "rbac", action: "read" },
    // { name: "Stages", href: "/stages", icon: QueueListIcon, module: "stages", action: "read" },
    { name: "Real-Time", href: "/realtime-dashboard", icon: ChartBarIcon, module: "realtime-dashboard", action: "read" },
    { name: "Work Status", href: "/admin/stage-status", icon: ClipboardDocumentCheckIcon, module: "admin_stage_status", action: "read" },
    { name: "Reports", href: "/reports", icon: DocumentChartBarIcon, module: "reports", action: "read" },
    // { name: "Count Master", href: "/count-master", icon: CalculatorIcon, module: "count_master", action: "read" },
    { name: "Masters", href: "/masters", icon: CircleStackIcon, module: "master", action: "read" },
    { name: "Settings", href: "/settings", icon: Cog6ToothIcon, module: "settings", action: "read" },

  ];

  return (
    <motion.div
      className={`bg-blue-900 dark:bg-slate-900 border-r border-blue-700 dark:border-slate-800 h-full flex flex-col sidebar-container shadow-2xl ${sidebarCollapsed ? "w-16" : "w-64"
        }`}
      animate={{ width: sidebarCollapsed ? 64 : 256 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)'
      }}
    >
      {/* Logo and brand */}
      <div
        className="flex items-center justify-between p-6 border-b border-blue-700 relative overflow-hidden min-h-[80px] cursor-pointer hover:bg-blue-600/90 active:scale-[0.98] transition-all duration-200"
        onClick={handleToggleCollapse}
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 50%, #2563eb 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-12 -translate-x-12"></div>
        </div>

        {!sidebarCollapsed && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center relative z-10"
          >
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center">
                <CompanyLogo
                  size="2xl"
                  variant="minimal"
                  animated={false}
                  showTagline={false}
                />
              </div>
              <div className="flex flex-col">
                <h1 className="text-white font-bold text-xl leading-tight drop-shadow-sm">
                  {BRAND_NAME}
                </h1>
                <p className="text-blue-100 text-md font-medium drop-shadow-sm">
                  {config.BRANCH_NAME}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {sidebarCollapsed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute inset-0 flex justify-center items-center relative z-10"
          >
            <div className="flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
              <CompanyLogo
                size="3xl"
                variant="minimal"
                animated={false}
                showTagline={false}
              />
            </div>
          </motion.div>
        )}

        {/* Control buttons */}
        <div className="flex items-center space-x-2 relative z-10">
          {/* Mobile close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCloseSidebar();
            }}
            className="lg:hidden p-2.5 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <XMarkIcon className="h-5 w-5 text-white" />
          </button>

          {/* Collapse/Expand button - hidden on mobile */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleCollapse();
            }}
            className="hidden lg:block p-2.5 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-105 active:scale-95 group"
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon className="h-5 w-5 text-white group-hover:text-blue-100 transition-colors" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5 text-white group-hover:text-blue-100 transition-colors" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto sidebar-nav">
        {navigationItems.map((item, index) => {
          const isActive = location.pathname === item.href;

          // Special handling for Work Orders - check if user has any orders permission (read, create, or update)
          if (item.module === 'orders' && item.requiresAnyPermission) {
            if (!canAccessOrders) {
              return null; // Don't render Work Orders menu if no permissions
            }
            // Render Work Orders menu directly (bypass PermissionGuard since we already checked permissions)
            return (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={item.href}
                  onClick={() => {
                    if (sidebarOpen && window.innerWidth < 1024) {
                      handleCloseSidebar();
                    }
                  }}
                  className={`group flex items-center rounded-2xl font-medium transition-all duration-300 relative overflow-hidden ${sidebarCollapsed
                    ? "justify-center px-2 py-4 text-base"
                    : "px-4 py-3 text-base min-h-[48px]"
                    } ${isActive
                      ? "bg-white/25 text-white shadow-lg border border-white/40"
                      : "text-blue-50 hover:text-white hover:bg-white/15"
                    }`}
                  style={{
                    minHeight: sidebarCollapsed ? '48px' : '48px',
                    display: 'flex',
                    alignItems: 'center',
                    ...(isActive && {
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.3)'
                    })
                  }}
                >
                  <motion.div
                    className="absolute inset-0 bg-white/0 group-hover:bg-white/10 rounded-2xl"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  />

                  <item.icon
                    className={`flex-shrink-0 relative z-10 transition-all duration-200 ${sidebarCollapsed
                      ? "h-6 w-6 mr-0"
                      : "h-6 w-6 mr-3"
                      } ${isActive
                        ? "text-white"
                        : "text-blue-100 group-hover:text-white"
                      }`}
                    style={{ minWidth: '24px', minHeight: '24px' }}
                  />

                  {!sidebarCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="relative z-10 truncate font-medium whitespace-nowrap"
                    >
                      {item.name}
                    </motion.span>
                  )}

                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-4 px-3 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-xl">
                      {item.name}
                    </div>
                  )}
                </Link>
              </motion.div>
            );
          }

          return (
            <PermissionGuard
              key={item.name}
              module={item.module}
              action={item.action}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={item.href}
                  onClick={() => {
                    // Only close sidebar on mobile when it's in overlay mode
                    // Desktop sidebar should stay open during navigation
                    if (sidebarOpen && window.innerWidth < 1024) {
                      handleCloseSidebar();
                    }
                  }}
                  className={`group flex items-center rounded-2xl font-medium transition-all duration-300 relative overflow-hidden ${sidebarCollapsed
                    ? "justify-center px-2 py-4 text-base"
                    : "px-4 py-3 text-base min-h-[48px]"
                    } ${isActive
                      ? "bg-white/25 text-white shadow-lg border border-white/40"
                      : "text-blue-50 hover:text-white hover:bg-white/15"
                    }`}
                  style={{
                    minHeight: sidebarCollapsed ? '48px' : '48px',
                    display: 'flex',
                    alignItems: 'center',
                    ...(isActive && {
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.3)'
                    })
                  }}
                >
                  {/* Hover effect */}
                  <motion.div
                    className="absolute inset-0 bg-white/0 group-hover:bg-white/10 rounded-2xl"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  />

                  <item.icon
                    className={`flex-shrink-0 relative z-10 transition-all duration-200 ${sidebarCollapsed
                      ? "h-6 w-6 mr-0"
                      : "h-6 w-6 mr-3"
                      } ${isActive
                        ? "text-white"
                        : "text-blue-100 group-hover:text-white"
                      }`}
                    style={{ minWidth: '24px', minHeight: '24px' }}
                  />

                  {!sidebarCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="relative z-10 truncate font-medium whitespace-nowrap"
                    >
                      {item.name}
                    </motion.span>
                  )}

                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-4 px-3 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-xl">
                      {item.name}
                    </div>
                  )}
                </Link>
              </motion.div>
            </PermissionGuard>
          );
        })}
      </nav>

      {/* Footer */}
      <div
        className="py-6 px-2 border-t border-blue-700"
        style={{
          background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.8) 0%, rgba(30, 64, 175, 0.6) 100%)'
        }}
      >
        {!sidebarCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-sm text-blue-50 flex justify-between items-center"
          >
            <span className="text-white ">v1.0.0</span>
            <span className="text-white">© 2025 {DEVELOPER_NAME}</span>

          </motion.div>
        )}
      </div>
    </motion.div>
  );
});

export default Sidebar;
